import { useState, useEffect } from 'react';
import { Search, AlertCircle, Music, Check, Filter } from 'lucide-react';
import axios from '@/lib/axios';
import { Song } from '@/types/song';
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { formatDurationWithHours } from "@/lib/utils";

interface AddSongsToPlaylistModalProps {
  isOpen: boolean;
  onClose: () => void;
  playlistId: string;
  onSuccess: () => void;
}

export function AddSongsToPlaylistModal({ 
  isOpen, 
  onClose, 
  playlistId,
  onSuccess 
}: AddSongsToPlaylistModalProps) {
  const [songs, setSongs] = useState<Song[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSongs, setSelectedSongs] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [addingInProgress, setAddingInProgress] = useState<boolean>(false);
  const [genres, setGenres] = useState<string[]>([]);
  const [albums, setAlbums] = useState<string[]>([]);
  const [selectedGenre, setSelectedGenre] = useState<string>('all');
  const [selectedAlbum, setSelectedAlbum] = useState<string>('all');
  const [showFilters, setShowFilters] = useState<boolean>(true);

  // Şarkıları yükle
  useEffect(() => {
    if (isOpen) {
      fetchSongs();
      // Modal açıldığında seçimi temizle
      setSelectedSongs([]);
      setSearchValue('');
      setSelectedGenre('all');
      setSelectedAlbum('all');
      // Filtreleme panelini otomatik olarak aç
      setShowFilters(true);
    }
  }, [isOpen]);

  const fetchSongs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Önce sayfa açıldığında kullanıcıya bilgi verelim
      setSongs([]);
      setGenres([]);
      setAlbums([]);
      
      const { data } = await axios.get('/songs', {
        params: {
          limit: 5000 // Daha fazla şarkı yükle (3000+ şarkı için yeterli)
        }
      });
      
      if (!data.songs || data.songs.length === 0) {
        setError('Hiç şarkı bulunamadı');
        setLoading(false);
        return;
      }
      
      console.log("Yüklenen şarkılar:", data.songs.length);
      
      // Tür ve albüm bilgilerini doğru şekilde çıkaralım
      const uniqueGenres = Array.from(new Set(
        data.songs
          .filter((song: Song) => song.genre && typeof song.genre === 'string' && song.genre.trim() !== '')
          .map((song: Song) => song.genre)
      )).sort();
      
      const uniqueAlbums = Array.from(new Set(
        data.songs
          .filter((song: Song) => song.album && typeof song.album === 'string' && song.album.trim() !== '')
          .map((song: Song) => song.album)
      )).sort();
      
      console.log("Bulunan türler:", uniqueGenres.length);
      console.log("Bulunan albümler:", uniqueAlbums.length);
      
      setGenres(uniqueGenres as string[]);
      setAlbums(uniqueAlbums as string[]);
      setSongs(data.songs || []);
    } catch (err) {
      console.error('Şarkılar yüklenirken hata:', err);
      setError('Şarkılar yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  // Şarkı seçme işlemi
  const handleSongSelection = (songId: string) => {
    setSelectedSongs(prev => {
      if (prev.includes(songId)) {
        return prev.filter(id => id !== songId);
      } else {
        return [...prev, songId];
      }
    });
  };

  // Seçili şarkıları playlist'e ekle
  const handleAddSongsToPlaylist = async () => {
    if (!playlistId) {
      setError('Playlist ID bulunamadı');
      return;
    }

    if (selectedSongs.length === 0) {
      setError('Lütfen en az bir şarkı seçin');
      return;
    }

    try {
      setAddingInProgress(true);
      setError(null);

      console.log(`Playlist ID: ${playlistId}'ye ${selectedSongs.length} şarkı ekleniyor`);

      // API isteği gönder
      await axios.post(`/playlists/${playlistId}/songs`, {
        songs: selectedSongs,
        updateForAllCompanies: true
      });

      // Başarılı mesajı göster
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Şarkılar eklenirken hata:', err);
      setError(
        err.response?.data?.message || 
        'Şarkılar playlist\'e eklenirken bir hata oluştu. Lütfen tekrar deneyin.'
      );
    } finally {
      setAddingInProgress(false);
    }
  };

  // Filtreleme işlemi
  const filteredSongs = songs
    .filter(song => {
      // Metin araması
      const matchesSearch = !searchValue.trim() || 
        (song.name && song.name.toLowerCase().includes(searchValue.toLowerCase())) ||
        (song.artist && song.artist.toLowerCase().includes(searchValue.toLowerCase())) ||
        (song.album && song.album.toLowerCase().includes(searchValue.toLowerCase()));
      
      // Tür filtresi
      const matchesGenre = selectedGenre === 'all' || !selectedGenre || song.genre === selectedGenre;
      
      // Albüm filtresi
      const matchesAlbum = selectedAlbum === 'all' || !selectedAlbum || song.album === selectedAlbum;
      
      return matchesSearch && matchesGenre && matchesAlbum;
    })
    .sort((a, b) => a.name.localeCompare(b.name)); // İsme göre sırala

  const resetFilters = () => {
    setSelectedGenre('all');
    setSelectedAlbum('all');
    setSearchValue('');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Playlist'e Şarkı Ekle</DialogTitle>
          <DialogDescription>
            Eklemek istediğiniz şarkıları seçin ({selectedSongs.length} şarkı seçildi)
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive" className="my-2">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="mt-2">
          <div className="mb-4 space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Şarkı ara (isim, sanatçı, albüm)..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="pl-10"
              />
              <Button 
                variant="ghost" 
                size="icon" 
                className={`absolute right-2 top-1/2 transform -translate-y-1/2 h-7 w-7 ${showFilters ? 'bg-slate-100' : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="h-4 w-4" />
              </Button>
            </div>
            
            {showFilters && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 border rounded-md bg-slate-50">
                <div>
                  <label className="text-sm font-medium mb-1 block">Tür</label>
                  <Select 
                    value={selectedGenre} 
                    onValueChange={setSelectedGenre}
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue placeholder="Tüm türler" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tüm türler</SelectItem>
                      {genres.map(genre => (
                        <SelectItem key={genre} value={genre}>{genre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-1 block">Albüm</label>
                  <Select 
                    value={selectedAlbum} 
                    onValueChange={setSelectedAlbum}
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue placeholder="Tüm albümler" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tüm albümler</SelectItem>
                      {albums.map(album => (
                        <SelectItem key={album} value={album}>{album}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="sm:col-span-2 flex justify-end">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={resetFilters}
                    className="text-xs h-7"
                  >
                    Filtreleri Temizle
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="border rounded-md">
            {loading ? (
              <div className="py-8 text-center">
                <div className="animate-pulse flex flex-col items-center">
                  <div className="h-12 w-12 bg-slate-200 rounded-full mb-4"></div>
                  <div className="h-4 w-32 bg-slate-200 rounded mb-3"></div>
                  <div className="h-3 w-48 bg-slate-200 rounded"></div>
                </div>
                <p className="text-muted-foreground mt-4">Şarkılar yükleniyor... Bu işlem biraz zaman alabilir.</p>
                <p className="text-xs text-muted-foreground mt-1">Büyük müzik katalogları için lütfen bekleyin.</p>
              </div>
            ) : filteredSongs.length === 0 ? (
              <div className="py-8 text-center">
                <Music className="mx-auto h-12 w-12 text-muted-foreground opacity-20 mb-2" />
                <p className="text-muted-foreground">
                  {(searchValue.trim() || selectedGenre !== 'all' || selectedAlbum !== 'all') 
                    ? 'Filtrelere uygun şarkı bulunamadı' 
                    : 'Henüz şarkı eklenmemiş'}
                </p>
                {(searchValue.trim() || selectedGenre !== 'all' || selectedAlbum !== 'all') && (
                  <Button 
                    variant="link" 
                    onClick={resetFilters}
                    className="mt-2"
                  >
                    Filtreleri Temizle
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between px-3 py-2 border-b bg-slate-50">
                <p className="text-sm font-medium">{filteredSongs.length} şarkı bulundu</p>
                {selectedSongs.length > 0 && (
                  <p className="text-sm text-muted-foreground">{selectedSongs.length} şarkı seçildi</p>
                )}
              </div>
            )}
            
            {!loading && filteredSongs.length > 0 && (
              <ScrollArea className="h-[300px]">
                <div className="p-1">
                  {filteredSongs.map((song) => (
                    <div
                      key={song._id}
                      className={`flex items-center justify-between p-2 rounded-md cursor-pointer hover:bg-slate-100 ${
                        selectedSongs.includes(song._id) ? 'bg-slate-100' : ''
                      }`}
                      onClick={() => handleSongSelection(song._id)}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center border ${
                          selectedSongs.includes(song._id) 
                            ? 'bg-primary border-primary text-white' 
                            : 'border-slate-300'
                        }`}>
                          {selectedSongs.includes(song._id) && <Check className="h-3 w-3" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{song.name}</p>
                          <div className="flex text-xs text-muted-foreground">
                            <p className="truncate">{song.artist || 'Bilinmeyen Sanatçı'}</p>
                            {song.album && (
                              <>
                                <span className="mx-1">•</span>
                                <p className="truncate">{song.album}</p>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground whitespace-nowrap">
                          {song.duration ? formatDurationWithHours(song.duration) : '-'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button 
            variant="outline" 
            onClick={onClose}
            disabled={addingInProgress}
          >
            İptal
          </Button>
          <Button 
            onClick={handleAddSongsToPlaylist} 
            disabled={selectedSongs.length === 0 || addingInProgress}
          >
            {addingInProgress 
              ? 'Ekleniyor...' 
              : `${selectedSongs.length} Şarkıyı Ekle`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
